import { Platform } from 'react-native';

// Paleta de cores principal
const colors = {
  // Cores principais
  primary: {
    light: '#7C9EF4', // Azul claro
    default: '#4F73E1', // Azul principal
    dark: '#3258C5', // Azul escuro
    contrast: '#FFFFFF', // Texto/iconografia sobre o primário
    gradient: ['#4F73E1', '#7C9EF4'] as [string, string],
  },

  // Cores secundárias
  secondary: {
    light: '#F4BB7C', // Laranja suave
    default: '#F09F45', // Laranja
    dark: '#D5812A', // Laranja escuro
    contrast: '#1A1D2B', // Texto sobre o secundário
    gradient: ['#F09F45', '#F4BB7C'] as [string, string],
  },

  // Cores de fundo
  background: {
    primary: '#FFFFFF', // Fundo branco principal
    secondary: '#F8F9FC', // Fundo cinza claro para áreas secundárias
    tertiary: '#F0F2F8', // Fundo para cards e elementos elevados
    dark: '#121212', // Modo escuro (opcional)
  },

  // Cores para textos
  text: {
    primary: '#1A1D2B', // Texto escuro para conteúdo principal
    secondary: '#5A6072', // Texto cinza para conteúdo secundário
    tertiary: '#8F95A9', // Texto claro para conteúdo terciário
    inverse: '#FFFFFF', // Texto branco para fundos escuros
  },

  // Cores de utilidade
  util: {
    success: '#34D399', // Verde para sucesso
    warning: '#FBBF24', // Amarelo para avisos
    error: '#F87171', // Vermelho para erros
    info: '#60A5FA', // Azul para informações
  },

  // Cores para overlays e modais
  overlay: {
    dark: 'rgba(0, 0, 0, 0.9)', // Fundo escuro para modais
    semi: 'rgba(0, 0, 0, 0.5)', // Fundo semi-transparente
  },

  // Cores para bordas e separadores
  border: {
    light: '#E2E8F0', // Borda clara
    default: '#CBD5E1', // Borda padrão
    dark: '#94A3B8', // Borda escura
  },

  // Branco absoluto para uso universal
  white: '#FFFFFF',
};

// Espaçamento consistente
const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,

  // Espaçamento específico para telas
  screen: {
    horizontal: 16,
    vertical: 16,
  },
};

// Tipografia
const fontSizes = {
  xxs: 10,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  xxl: 24,
  xxxl: 32,
};

// Definição dos pesos de fonte
const fontWeights = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
};

// Família de fontes (pode ser ajustada conforme necessidade)
const fontFamily = Platform.select({
  ios: {
    regular: 'System',
    medium: 'System',
    bold: 'System',
  },
  android: {
    regular: 'Roboto',
    medium: 'Roboto-Medium',
    bold: 'Roboto-Bold',
  },
  web: {
    regular: 'Roboto, system-ui, sans-serif',
    medium: 'Roboto-Medium, system-ui, sans-serif',
    bold: 'Roboto-Bold, system-ui, sans-serif',
  },
});

// Configurações de borda
const borderRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 9999,
  circular: 9999,
};

// Sombras
const shadows = {
  sm: Platform.select({
    ios: {
      shadowColor: colors.text.primary,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
    },
    android: {
      elevation: 1,
    },
    web: {
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    },
  }),
  md: Platform.select({
    ios: {
      shadowColor: colors.text.primary,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    android: {
      elevation: 3,
    },
    web: {
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
  }),
  lg: Platform.select({
    ios: {
      shadowColor: colors.text.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 8,
    },
    android: {
      elevation: 5,
    },
    web: {
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
  }),
};

// Z-index para controle de sobreposição de elementos
const zIndex = {
  base: 0,
  card: 10,
  dropdown: 20,
  modal: 30,
  overlay: 40,
  toast: 50,
};

// Configurações específicas de componentes
const components = {
  checklistEditor: {
    completeBackground: '#15803D',
    spacing: {
      labelGap: 6,
      cardPadding: 12,
      compactGap: 5,
      footerButtonHorizontal: 6,
    },
    fontSizes: { supporting: 11 },
    borderRadius: { footerButton: 10 },
  },
  modal: {
    photoViewer: {
      containerSize: {
        width: '90%' as const,
        height: '80%' as const,
      },
      closeButton: {
        size: 40,
        position: {
          top: spacing.md + spacing.xs, // 20
          right: spacing.md + spacing.xs, // 20
        },
      },
    },
  },
};

// Configurações de animação
const animation = {
  durations: {
    fast: 200,
    normal: 300,
    slow: 500,
  },
  easing: {
    // Nomes das funções de easing para React Native Animated ou Reanimated
    // Podem ser implementados conforme necessidade
    easeIn: 'easeIn',
    easeOut: 'easeOut',
    easeInOut: 'easeInOut',
  },
};

// Exportar objeto completo de tema para uso em toda a aplicação
export const theme = {
  colors,
  spacing,
  fontSizes,
  fontWeights,
  fontFamily,
  borderRadius,
  shadows,
  zIndex,
  animation,
  components,
};

// Exportar componentes individuais para facilitar o uso
export default theme; 
